/**
 * Automations — Monday-style declarative rules UI (Phase 3, 2026-05-16).
 *
 * Każdy user może stworzyć regułę: "Kiedy X, jeżeli Y, to Z".
 * Triggery = event_types z activity_logger (24 typy).
 * Actions = log_event / change_stage / add_note / notify_user / send_email (draft).
 *
 * Bezpieczeństwo: send_email NIE wysyła automatycznie — tworzy draft, user reviewuje.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Zap, Plus, Trash2, Pencil, PlayCircle, PauseCircle,
  Sparkles, ListChecks, Loader2, X as XIcon, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listAutomationRules, createAutomationRule, updateAutomationRule,
  deleteAutomationRule, toggleAutomationRule, seedPrebuiltAutomations,
  listAutomationActions, listAutomationEventTypes,
} from '../lib/api';
import PlanLock from '../components/common/PlanLock';


function emptyRule() {
  return {
    name: '',
    description: '',
    enabled: true,
    trigger: { event_type: 'stage_changed', item_type: 'investment' },
    conditions: [],
    actions: [{ type: 'add_note', params: { text: '' } }],
    min_seconds_between_runs: 0,
  };
}


function ConditionEditor({ cond, onChange, onRemove }) {
  return (
    <div className="flex gap-1.5 items-center bg-muted/30 p-1.5 rounded">
      <Input
        placeholder="pole (np. payload.to)"
        value={cond.field || ''}
        onChange={(e) => onChange({ ...cond, field: e.target.value })}
        className="h-7 text-xs flex-1"
      />
      <Select
        value={cond.op || 'eq'}
        onValueChange={(v) => onChange({ ...cond, op: v })}
      >
        <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {['eq', 'neq', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'contains', 'exists'].map(op => (
            <SelectItem key={op} value={op}>{op}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        placeholder="wartość"
        value={typeof cond.value === 'object' ? JSON.stringify(cond.value) : (cond.value ?? '')}
        onChange={(e) => onChange({ ...cond, value: e.target.value })}
        className="h-7 text-xs flex-1"
      />
      <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 w-7 p-0">
        <XIcon className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}


function ActionEditor({ action, onChange, onRemove, actionTypes }) {
  const schema = actionTypes.find(a => a.type === action.type);
  const params = action.params || {};
  return (
    <div className="bg-muted/30 p-2 rounded space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Select
          value={action.type}
          onValueChange={(v) => onChange({ ...action, type: v, params: {} })}
        >
          <SelectTrigger className="h-7 text-xs w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {actionTypes.map(a => (
              <SelectItem key={a.type} value={a.type}>
                {a.label_pl || a.type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 w-7 p-0 ml-auto">
          <XIcon className="w-3.5 h-3.5" />
        </Button>
      </div>
      {schema && Object.entries(schema.params_schema || {}).map(([key, spec]) => (
        <div key={key}>
          {spec.enum ? (
            <Select
              value={params[key] || ''}
              onValueChange={(v) => onChange({ ...action, params: { ...params, [key]: v } })}
            >
              <SelectTrigger className="h-7 text-xs">
                <SelectValue placeholder={key} />
              </SelectTrigger>
              <SelectContent>
                {spec.enum.map(v => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              placeholder={key}
              value={params[key] || ''}
              onChange={(e) => onChange({ ...action, params: { ...params, [key]: e.target.value } })}
              className="h-7 text-xs"
            />
          )}
        </div>
      ))}
    </div>
  );
}


function RuleEditor({ open, onClose, onSave, initial, eventTypes, actionTypes }) {
  const [rule, setRule] = useState(initial || emptyRule());
  useEffect(() => { setRule(initial || emptyRule()); }, [initial]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!rule.name || !rule.trigger?.event_type || !rule.actions?.length) {
      toast.error('Nazwa, trigger i ≥1 action są wymagane');
      return;
    }
    setSaving(true);
    try {
      await onSave(rule);
      onClose();
    } catch (err) {
      toast.error('Zapis nie powiódł się');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edytuj regułę' : 'Nowa reguła automation'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Nazwa</Label>
            <Input
              value={rule.name}
              onChange={(e) => setRule({ ...rule, name: e.target.value })}
              placeholder="✨ Auto-track po spec-in"
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Opis (opcjonalnie)</Label>
            <Textarea
              value={rule.description || ''}
              onChange={(e) => setRule({ ...rule, description: e.target.value })}
              rows={2}
              className="text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Trigger (event)</Label>
              <Select
                value={rule.trigger?.event_type}
                onValueChange={(v) => setRule({ ...rule, trigger: { ...rule.trigger, event_type: v } })}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {eventTypes.map(et => (
                    <SelectItem key={et.key} value={et.key}>
                      {et.icon} {et.label_pl || et.key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Item type (filter)</Label>
              <Select
                value={rule.trigger?.item_type || '__all__'}
                onValueChange={(v) => setRule({ ...rule, trigger: { ...rule.trigger, item_type: v === '__all__' ? null : v } })}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Wszystkie</SelectItem>
                  <SelectItem value="investment">investment</SelectItem>
                  <SelectItem value="tender">tender</SelectItem>
                  <SelectItem value="supplier">supplier</SelectItem>
                  <SelectItem value="contractor">contractor</SelectItem>
                  <SelectItem value="rfq_thread">rfq_thread</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Warunki (AND, opcjonalne)</Label>
              <Button
                variant="ghost" size="sm"
                className="h-6 text-xs gap-1"
                onClick={() => setRule({ ...rule, conditions: [...(rule.conditions || []), { field: '', op: 'eq', value: '' }] })}
              >
                <Plus className="w-3 h-3" /> dodaj
              </Button>
            </div>
            <div className="space-y-1.5">
              {(rule.conditions || []).map((c, i) => (
                <ConditionEditor
                  key={i}
                  cond={c}
                  onChange={(nc) => {
                    const next = [...rule.conditions];
                    next[i] = nc;
                    setRule({ ...rule, conditions: next });
                  }}
                  onRemove={() => setRule({ ...rule, conditions: rule.conditions.filter((_, j) => j !== i) })}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Akcje (w kolejności)</Label>
              <Button
                variant="ghost" size="sm"
                className="h-6 text-xs gap-1"
                onClick={() => setRule({ ...rule, actions: [...(rule.actions || []), { type: 'add_note', params: {} }] })}
              >
                <Plus className="w-3 h-3" /> dodaj
              </Button>
            </div>
            <div className="space-y-1.5">
              {(rule.actions || []).map((a, i) => (
                <ActionEditor
                  key={i}
                  action={a}
                  actionTypes={actionTypes}
                  onChange={(na) => {
                    const next = [...rule.actions];
                    next[i] = na;
                    setRule({ ...rule, actions: next });
                  }}
                  onRemove={() => setRule({ ...rule, actions: rule.actions.filter((_, j) => j !== i) })}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Włączone</Label>
            <Switch
              checked={rule.enabled}
              onCheckedChange={(v) => setRule({ ...rule, enabled: v })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            Zapisz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Automations({ lang = 'pl' }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, et, at] = await Promise.all([
        listAutomationRules(),
        listAutomationEventTypes(),
        listAutomationActions(),
      ]);
      setRules(r.items || []);
      setEventTypes(et.event_types || []);
      setActionTypes(at.actions || []);
    } catch (err) {
      toast.error('Nie udało się załadować reguł');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (rule) => {
    if (rule.id) {
      await updateAutomationRule(rule.id, rule);
      toast.success('Reguła zaktualizowana');
    } else {
      await createAutomationRule(rule);
      toast.success('Reguła utworzona');
    }
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunąć regułę?')) return;
    try {
      await deleteAutomationRule(id);
      toast.success('Usunięto');
      load();
    } catch { toast.error('Nie udało się usunąć'); }
  };

  const handleToggle = async (id) => {
    try {
      const r = await toggleAutomationRule(id);
      toast.success(r.enabled ? 'Włączono' : 'Wyłączono');
      load();
    } catch { toast.error('Toggle failed'); }
  };

  const handleSeed = async () => {
    try {
      const r = await seedPrebuiltAutomations();
      toast.success(`Dodano ${r.inserted} reguł best-practices`);
      load();
    } catch { toast.error('Seed failed'); }
  };

  return (
    <PlanLock feature="automation_module" requiredPlan="pro" lang={lang}>
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-300" />
            Automation Engine
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Zdefiniuj reguły "Gdy X → Zrób Y". 24 typy zdarzeń, 5 typów akcji.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeed} className="gap-1" data-testid="seed-prebuilt">
            <Sparkles className="w-4 h-4" />
            Dodaj 10 reguł best-practices
          </Button>
          <Button
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="gap-1"
            data-testid="new-rule-btn"
          >
            <Plus className="w-4 h-4" />
            Nowa reguła
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Brak reguł automation</p>
            <p className="text-xs mt-1">
              Kliknij "Dodaj 10 reguł best-practices" żeby zacząć szybko.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="rules-list">
          {rules.map(rule => (
            <Card key={rule.id} className={!rule.enabled ? 'opacity-60' : ''}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{rule.name}</span>
                      {rule.is_prebuilt && (
                        <Badge variant="outline" className="text-[9px] h-4">prebuilt</Badge>
                      )}
                      <Badge variant="outline" className="text-[9px] h-4">
                        run × {rule.run_count || 0}
                      </Badge>
                      <Badge
                        className={`text-[9px] h-4 ${rule.enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-zinc-500/15 text-zinc-300'}`}
                      >
                        {rule.enabled ? 'aktywna' : 'wyłączona'}
                      </Badge>
                    </div>
                    {rule.description && (
                      <div className="text-xs text-muted-foreground mt-1">{rule.description}</div>
                    )}
                    <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1 flex-wrap">
                      <Badge variant="outline" className="text-[9px] h-4 gap-0.5">
                        <ListChecks className="w-2.5 h-2.5" />
                        {rule.trigger?.event_type}
                        {rule.trigger?.item_type ? ` · ${rule.trigger.item_type}` : ''}
                      </Badge>
                      <ChevronRight className="w-3 h-3" />
                      <span>
                        {(rule.actions || []).map(a => a.type).join(' + ')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(rule.id)}
                      className="h-7 w-7 p-0"
                      title={rule.enabled ? 'Wyłącz' : 'Włącz'}
                    >
                      {rule.enabled ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditing(rule); setEditorOpen(true); }}
                      className="h-7 w-7 p-0"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(rule.id)}
                      className="h-7 w-7 p-0 text-red-300 hover:text-red-200"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RuleEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        initial={editing}
        eventTypes={eventTypes}
        actionTypes={actionTypes}
      />
    </div>
    </PlanLock>
  );
}
